import { SearchWidgetContext } from '@/context/SearchWidgetContext';
import {
  getNonTripOffers,
  resetPurchase,
} from '@/features/purchase/purchaseActions';
import { useDispatch } from '@/store/utils';
import { formatFullDate } from '@/utils/dateTime';
import { GTM_EVENT, trackCustomEvent } from '@/utils/gtm';
import type { SearchFormValues } from '@packages/webshop-search-widget';
import type { FC, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const SearchWidgetProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isSearchOverlayOpened, setIsSearchOverlayOpened] = useState(false);
  const [currentSearchFormValues, setCurrentSearchFormValues] = useState<
    SearchFormValues | undefined
  >();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    async (values: SearchFormValues) => {
      setCurrentSearchFormValues(values);
      trackCustomEvent(GTM_EVENT.SEARCH_INITIATED);

      const isTicketProductType = values.productType === 'ticket';

      dispatch(resetPurchase());
      setIsSearchOverlayOpened(false);

      if (!values.origin?.value || !values.destination?.value) {
        navigate('/purchase');
        return;
      }

      if (isTicketProductType) {
        // Navigate immediately to show skeletons while loading
        navigate('/purchase/outbound/journey');
        // Data fetching will happen in OutboundJourneyList component
      } else {
        const offersResponse = await dispatch(
          getNonTripOffers({
            originStop: { code: values.origin.value },
            destinationStop: { code: values.destination.value },
            validityStartTime: formatFullDate(values.departureDate),
            passengers: values.passengers,
            promoCodes: values.promoCodes,
          })
        ).unwrap();

        if ((offersResponse?.nonTripOffers?.length ?? 0) > 0) {
          navigate('/purchase/non-trip/offer');
        }
      }
    },
    [dispatch, navigate]
  );

  return (
    <SearchWidgetContext.Provider
      value={{
        isSearchOverlayOpened,
        setIsSearchOverlayOpened,
        currentSearchFormValues,
        setCurrentSearchFormValues,
        handleSubmit,
      }}
    >
      {children}
    </SearchWidgetContext.Provider>
  );
};
